import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Ban,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Plus,
  Shield,
  ShieldOff,
  Truck,
  Clock,
  User,
  FileText,
  Search,
  X,
} from "lucide-react";


// Status badge component
function StatusBadge({ status }) {
  const config = {
    pending: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, label: "Pending Approval" },
    approved: { color: "bg-red-100 text-red-800 border-red-200", icon: Ban, label: "Blacklisted" },
    rejected: { color: "bg-slate-100 text-slate-800 border-slate-200", icon: XCircle, label: "Rejected" },
    whitelisted: { color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle, label: "Whitelisted" },
  };
  
  const { color, icon: Icon, label } = config[status] || config.pending;
  
  return (
    <Badge className={`${color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// Blacklist Request Modal
function BlacklistRequestModal({ isOpen, onClose, onSubmit, isAdmin, token }) {
  const [formData, setFormData] = useState({
    company_name: "",
    company_email: "",
    company_phone: "",
    reason: "",
    pantheon_customer_id: null,
    pantheon_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [searchingVendors, setSearchingVendors] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Debounced CRM (Pantheon) vendor search
  useEffect(() => {
    const q = formData.company_name.trim();
    if (q.length < 2 || formData.pantheon_customer_id) {
      setVendorSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearchingVendors(true);
        const r = await axios.get(`${API_URL}/crm/search-companies`, {
          params: { query: q, limit: 8 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setVendorSuggestions(r.data.companies || []);
        setShowSuggestions(true);
      } catch {
        setVendorSuggestions([]);
      } finally {
        setSearchingVendors(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [formData.company_name, formData.pantheon_customer_id, token]);

  const pickVendor = (v) => {
    setFormData((f) => ({
      ...f,
      company_name: v.name,
      company_email: v.email || f.company_email,
      pantheon_customer_id: v.id,
      pantheon_name: v.name,
    }));
    setShowSuggestions(false);
    setVendorSuggestions([]);
  };

  const clearVendorLink = () => {
    setFormData((f) => ({ ...f, pantheon_customer_id: null, pantheon_name: "" }));
  };

  const handleSubmit = async () => {
    if (!formData.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!formData.reason.trim()) {
      toast.error("Please provide a reason for blacklisting");
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({
        company_name: "",
        company_email: "",
        company_phone: "",
        reason: "",
        pantheon_customer_id: null,
        pantheon_name: "",
      });
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" />
            {isAdmin ? "Blacklist Trucking Company" : "Request Blacklist"}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {isAdmin ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <Shield className="h-4 w-4 inline mr-2" />
              As admin, this will <strong>blacklist immediately</strong>. No second-step approval needed.
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              This request will be sent to admin for approval before the company is blacklisted.
            </div>
          )}

          <div className="relative">
            <Label>Company Name * <span className="text-xs text-slate-400 font-normal">(start typing to search Pantheon vendors)</span></Label>
            <div className="relative">
              <Input
                value={formData.company_name}
                onChange={(e) => {
                  setFormData({ ...formData, company_name: e.target.value, pantheon_customer_id: null, pantheon_name: "" });
                }}
                onFocus={() => formData.company_name.length >= 2 && setShowSuggestions(true)}
                placeholder="Start typing — e.g. Albatros, Kristl, Trans Europa…"
                data-testid="blacklist-company-name"
                autoComplete="off"
              />
              {searchingVendors && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
              )}
            </div>
            {formData.pantheon_customer_id && (
              <div className="mt-1 flex items-center gap-2 text-xs text-emerald-700">
                <CheckCircle className="h-3 w-3" />
                Linked to Pantheon vendor
                <button type="button" onClick={clearVendorLink} className="text-slate-500 hover:text-red-600 underline">change</button>
              </div>
            )}
            {showSuggestions && vendorSuggestions.length > 0 && !formData.pantheon_customer_id && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto" data-testid="vendor-suggestions">
                {vendorSuggestions.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => pickVendor(v)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 text-sm"
                    data-testid={`vendor-suggestion-${v.id}`}
                  >
                    <div className="font-medium text-slate-900">{v.name}</div>
                    <div className="text-xs text-slate-500">
                      {[v.country, v.city, v.email].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showSuggestions && !searchingVendors && vendorSuggestions.length === 0 && formData.company_name.length >= 2 && !formData.pantheon_customer_id && (
              <div className="mt-1 text-xs text-slate-500 italic">
                No matching Pantheon vendor — will be blacklisted as a free-text name.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email (Optional)</Label>
              <Input
                value={formData.company_email}
                onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                placeholder="company@email.com"
              />
            </div>
            <div>
              <Label>Phone (Optional)</Label>
              <Input
                value={formData.company_phone}
                onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                placeholder="+386 ..."
              />
            </div>
          </div>

          <div>
            <Label>Reason for Blacklisting *</Label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Explain why this company should be blacklisted..."
              rows={4}
              data-testid="blacklist-reason"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="submit-blacklist-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isAdmin ? "Blacklist Now" : "Submit Request"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Approval Modal for Admin
function ApprovalModal({ isOpen, onClose, entry, onApprove }) {
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const handleAction = async (approved) => {
    setProcessing(true);
    try {
      await onApprove(entry.id, approved, notes);
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen || !entry) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Review Blacklist Request</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="font-semibold text-lg">{entry.company_name}</div>
            {entry.company_email && <div className="text-sm text-slate-600">{entry.company_email}</div>}
            {entry.company_phone && <div className="text-sm text-slate-600">{entry.company_phone}</div>}
          </div>

          <div>
            <Label className="text-slate-500">Reason for Blacklisting</Label>
            <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {entry.reason}
            </div>
          </div>

          <div className="text-sm text-slate-500">
            Requested by <span className="font-medium">{entry.requested_by}</span> on{" "}
            {new Date(entry.requested_at).toLocaleDateString()}
          </div>

          <div>
            <Label>Admin Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about your decision..."
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={() => handleAction(false)} 
            disabled={processing}
            variant="outline"
            className="border-slate-300"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
          <Button 
            onClick={() => handleAction(true)} 
            disabled={processing}
            className="bg-red-600 hover:bg-red-700"
          >
            {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Ban className="h-4 w-4 mr-2" />
            Approve Blacklist
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TruckingBlacklistPage() {
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [blacklist, setBlacklist] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const fetchBlacklist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trucking-blacklist?status=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlacklist(response.data.blacklisted_companies || []);
      setPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error("Error fetching blacklist:", error);
      toast.error("Failed to load blacklist");
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const handleSubmitRequest = async (data) => {
    try {
      const response = await axios.post(`${API_URL}/trucking-blacklist/request`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBlacklist();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
      throw error;
    }
  };

  const handleApprove = async (entryId, approved, notes) => {
    try {
      await axios.put(
        `${API_URL}/trucking-blacklist/${entryId}/approve`,
        { approved, admin_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approved ? "Company blacklisted" : "Request rejected");
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process request");
    }
  };

  const handleWhitelist = async (entryId) => {
    if (!window.confirm("Are you sure you want to remove this company from the blacklist?")) return;
    
    try {
      await axios.put(
        `${API_URL}/trucking-blacklist/${entryId}/whitelist`,
        { reason: "Removed by admin" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Company removed from blacklist");
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to whitelist");
    }
  };

  const filteredBlacklist = blacklist.filter(entry =>
    entry.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.reason?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Shield className="h-7 w-7 text-red-600" />
              Trucking Company Blacklist
            </h1>
            <p className="text-slate-600 mt-1">
              Manage blacklisted trucking companies
            </p>
          </div>
          <Button 
            onClick={() => setShowRequestModal(true)}
            className="bg-red-600 hover:bg-red-700"
            data-testid="request-blacklist-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {isAdmin ? "Blacklist Company" : "Request Blacklist"}
          </Button>
        </div>

        {/* Pending Approvals Alert for Admin */}
        {isAdmin && pendingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="text-amber-800">
                <strong>{pendingCount}</strong> blacklist request{pendingCount > 1 ? "s" : ""} pending your approval
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="border-amber-400 text-amber-700"
              onClick={() => setActiveTab("pending")}
            >
              Review Now
            </Button>
          </div>
        )}

        {/* Tabs and Search */}
        <div className="flex items-center justify-between mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="approved" className="text-red-700">
                Blacklisted
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="pending" className="text-amber-700">
                  Pending ({pendingCount})
                </TabsTrigger>
              )}
              <TabsTrigger value="whitelisted" className="text-green-700">
                Whitelisted
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>

        {/* Blacklist Table */}
        <Card>
          <CardContent className="p-0">
            {filteredBlacklist.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No companies found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredBlacklist.map((entry) => (
                  <div key={entry.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-lg">{entry.company_name}</span>
                          <StatusBadge status={entry.status} />
                        </div>
                        
                        {(entry.company_email || entry.company_phone) && (
                          <div className="text-sm text-slate-500 mt-1">
                            {entry.company_email && <span className="mr-4">{entry.company_email}</span>}
                            {entry.company_phone && <span>{entry.company_phone}</span>}
                          </div>
                        )}
                        
                        <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                          <span className="text-slate-500">Reason:</span>{" "}
                          <span className="text-slate-700">{entry.reason}</span>
                        </div>
                        
                        <div className="mt-2 text-xs text-slate-500 flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Requested by {entry.requested_by?.split("@")[0]}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(entry.requested_at).toLocaleDateString()}
                          </span>
                          {entry.approved_by && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {entry.status === "approved" ? "Approved" : "Processed"} by {entry.approved_by?.split("@")[0]}
                            </span>
                          )}
                        </div>
                        
                        {entry.admin_notes && (
                          <div className="mt-2 text-sm text-slate-600 italic">
                            Admin notes: {entry.admin_notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Admin actions for pending entries */}
                        {isAdmin && entry.status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => { setSelectedEntry(entry); setShowApprovalModal(true); }}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            Review
                          </Button>
                        )}
                        
                        {/* Admin can whitelist approved entries */}
                        {isAdmin && entry.status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleWhitelist(entry.id)}
                            className="border-green-400 text-green-700 hover:bg-green-50"
                          >
                            <ShieldOff className="h-4 w-4 mr-1" />
                            Whitelist
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Blacklist Rules:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Admin can blacklist a company <strong>immediately</strong>, no approval step</li>
                  <li>Staff requests go to admin for approval before taking effect</li>
                  <li>Pick a Pantheon vendor from the autocomplete to also block any future name variants linked to the same CRM record</li>
                  <li>Transport Orders to blacklisted companies require admin approval</li>
                  <li>Only admin can remove a company from the blacklist</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <BlacklistRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSubmit={handleSubmitRequest}
        isAdmin={isAdmin}
        token={token}
      />
      
      <ApprovalModal
        isOpen={showApprovalModal}
        onClose={() => { setShowApprovalModal(false); setSelectedEntry(null); }}
        entry={selectedEntry}
        onApprove={handleApprove}
      />
    </div>
  );
}
